/**
 * Date Utility Functions
 * Helper functions for date formatting and manipulation
 */

// All learner-facing timestamps render as Singapore wall-clock time (see #874).
// Hardcoded via Intl timeZone rather than a UTC+8 shift so DST-safe by construction.
const SGT_TIME_ZONE = 'Asia/Singapore';

/**
 * Formats a date using the Singapore locale (en-SG) in Singapore time
 * (Asia/Singapore). This is the standard locale for all date formatting in the
 * application, and learner-facing timestamps must read as SGT regardless of
 * the device/runtime timezone (see #874).
 *
 * @param date - The date to format
 * @param options - Intl.DateTimeFormatOptions for customizing the output
 * @returns Formatted date string using en-SG locale in Singapore time
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-SG', {
    ...options,
    // Force SGT unless the caller explicitly overrides the timezone.
    timeZone: options?.timeZone ?? SGT_TIME_ZONE,
  });
}

/**
 * Formats a date as Singapore wall-clock time (Asia/Singapore), independent of
 * the device/runtime timezone. The app is SG-only and every learner-facing
 * timestamp must render as SGT (see #874).
 *
 * @param date - The date to format
 * @param options - Intl.DateTimeFormatOptions (timeZone is forced to Asia/Singapore)
 * @returns Formatted string in Singapore time
 */
export function formatSGT(date: Date | string, options: Intl.DateTimeFormatOptions): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  // Intl defers day-period casing to CLDR and runtimes disagree: browsers and
  // Node follow en-SG (en-001) lowercase 'am'/'pm' while Bun/JSC emits
  // 'AM'/'PM'. Normalize to the SG lowercase convention so output (and the
  // discovery time-group anchors keyed on it) is runtime-independent. Only the
  // day-period token matches, so date text is never touched. Keep toLocaleString
  // as the producer: formatToParts exposes raw CLDR literals (U+202F) that
  // format() itself never emits.
  return dateObj
    .toLocaleString('en-SG', { ...options, timeZone: SGT_TIME_ZONE })
    .replace(/\b(am|pm)\b/gi, (token) => token.toLowerCase());
}

/**
 * Singapore civil calendar day key 'YYYY-MM-DD' — the discovery "selected day"
 * key shared by the SSR loader and the client (see #874). Both sides derive it
 * from the same instant, so the SSR-prefetched query key always matches the
 * hydrated page, even across the SGT midnight boundary.
 */
export function toSGTCivilKey(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const parts = SGT_CIVIL_DAY.formatToParts(dateObj);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * Inverse of toSGTCivilKey: the instant whose SGT wall-clock date is the key
 * (SGT midnight UTC+8). Lets the client seed its selected day from the key the
 * SSR loader computed, so hydration can never drift to a different SGT day.
 */
export function sgtCivilKeyToDate(key: string): Date {
  return new Date(`${key}T00:00:00Z`);
}

// Cached: Intl.DateTimeFormat construction is expensive and the civil-day
// helpers run once per calendar cell (COR-04).
const SGT_CIVIL_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: SGT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Today as an SGT civil day, in the sgtCivilKeyToDate shape: the instant at
 * UTC midnight of the current Singapore day. Calendar grids represent SGT days
 * in this shape and do their math in UTC accessors, so "today", week anchors,
 * and cell labels stay device-timezone independent (COR-04).
 */
export function sgtToday(): Date {
  return sgtCivilKeyToDate(toSGTCivilKey(new Date()));
}

/**
 * Short review date 'Jan 1, 2026' rendered in Singapore time. Kept in US order
 * because review cards shipped that shape (date-fns 'MMM d, yyyy'); only the
 * timezone was wrong (see #874).
 */
export function formatSGTShortDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: SGT_TIME_ZONE,
  });
}
